import { Box, Grid, Link, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';

import { ContentfulBackground, Maybe } from '@/types';
import {
	AnalyticsContext,
	BlogDetailsPageSummary,
	ContentTypeRichTextContent,
	ImageAsset as TImageAsset,
} from '@/graphql/__generated/sdk';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import { customEvent, resolveContentfulBgColor, translateTagColorNameForContentful } from '@/utils';
import { useAppContext } from '@/context';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import ContentfulButton from '@/components/ContentfulButton';

import { IBlogAuthorProps } from '../BlogAuthorDetails';
import { BlogImage, IBlogDetailsPage } from '../BlogDetailsPage';
import BlogDetailsImageAssetContainer from '../BlogDetailsImageAssetContainer';

import BlogDetailsHeroAuthorList from './BlogDetailsHeroAuthorList';
import {
	blogDetailsHeroBackLinkStyles,
	blogDetailsHeroImageWrapperStyles,
	blogDetailsHeroInfoStyles,
	blogDetailsHeroLeftStyles,
	blogDetailsHeroRightStyles,
	blogDetailsHeroTakeAwaysStyles,
	blogDetailsHeroWrapperStyles,
	heroContentBoxStyles,
} from './BlogDetailsHero.styles';
export interface IBlogDetailsHeroProps {
	author?: IBlogAuthorProps;
	reviewer?: IBlogAuthorProps;
	date: string;
	timeToRead: number;
	summary: Maybe<BlogDetailsPageSummary>;
	blogImage: BlogImage;
	imageAsset?: Maybe<TImageAsset>;
	isSmallScreen: boolean;
	title: string;
	heroBackgroundColor?: IBlogDetailsPage['heroBackgroundColor'];
	theme: Theme;
	heroContentTitle?: IBlogDetailsPage['heroContentTitle'];
	heroContentCollection?: IBlogDetailsPage['heroContentCollection'];
	heroButtonCollection?: IBlogDetailsPage['heroButtonCollection'];
}

const analyticsClick = (context: Maybe<AnalyticsContext> | undefined) => {
	if (context) {
		customEvent('tagEvent', context, context.eventName);
	}
};

const BlogDetailsHeroMainLink = ({ textLinkColor, blogRootPath }: { textLinkColor: string; blogRootPath: string }) => (
	<Link
		data-testid="blog_details_hero_mainlink"
		color={textLinkColor}
		href={blogRootPath}
		underline="hover"
		sx={blogDetailsHeroBackLinkStyles}
	>
		<TAGSvgIcon
			icon="ChevronLeftFunc"
			size={24}
			style={{ display: 'inline-block', transform: 'translate(0, -.15rem)', marginRight: '.25rem' }}
		/>
		<Typography color="inherit" variant="bodySmallBook">
			Blog Main Page
		</Typography>
	</Link>
);

export default function BlogDetailsHero({
	date,
	timeToRead,
	blogImage,
	imageAsset,
	title,
	summary,
	author,
	reviewer,
	heroBackgroundColor = 'white',
	theme,
	isSmallScreen,
	heroContentTitle,
	heroContentCollection,
	heroButtonCollection,
}: Readonly<IBlogDetailsHeroProps>) {
	const { config } = useAppContext();
	const blogRootPath = config.features.blogs.root;
	const formattedDate = format(new Date(date), 'MMMM dd, yyyy');

	const isDark =
		heroBackgroundColor?.toLowerCase().includes('dark') || heroBackgroundColor?.toLowerCase() === 'saffron';
	const contentfulSafeBackgroundColor = translateTagColorNameForContentful(heroBackgroundColor || '') || '';
	const contentfulBackgroundColor = resolveContentfulBgColor(
		contentfulSafeBackgroundColor as ContentfulBackground,
		theme
	);

	const textColor = isDark ? 'text.light' : 'text.secondary';
	const textLinkColor = isDark ? 'text.light' : 'text.interactive';
	const hasButtons = !!heroButtonCollection?.items?.length;
	const hasSubContent = !!heroContentCollection?.items?.length;

	return (
		<Grid
			data-testid="blog_details_hero"
			container
			justifyContent="flex-start"
			sx={blogDetailsHeroWrapperStyles(contentfulBackgroundColor)}
		>
			<Grid data-testid="blog_details_hero_left" item xs={12} md={6} sx={blogDetailsHeroLeftStyles}>
				<BlogDetailsHeroMainLink textLinkColor={textLinkColor} blogRootPath={blogRootPath} />
				<Box sx={blogDetailsHeroInfoStyles}>
					<Typography color={'text.secondary'} variant="bodySmallBook" component={'span'}>
						{timeToRead} min read
					</Typography>
					<Typography color={'text.secondary'} variant="bodySmallBook" component={'span'}>
						|
					</Typography>
					<Typography
						data-testid="blog_details_date"
						color={'text.secondary'}
						variant="bodySmallBook"
						component={'span'}
					>
						Published on: {formattedDate}
					</Typography>
				</Box>
				<Box marginBottom={'1.5rem'}>
					{title && title !== '' && (
						<Typography
							component={'h1'}
							data-testid="blog_details_hero_title"
							color={textColor}
							variant="heroMedium"
						>
							{title}
						</Typography>
					)}
				</Box>
				<BlogDetailsHeroAuthorList author={author} reviewer={reviewer} textColor={textColor} theme={theme} />
				<Box sx={blogDetailsHeroTakeAwaysStyles}>
					<Typography color={textColor} variant="bodyLargeBook" sx={{ marginBottom: '.5rem' }} component="p">
						KEY TAKEAWAYS
					</Typography>
					{summary?.json && (
						<ContentTypeRichText
							content={summary as ContentTypeRichTextContent}
							contentMaxWidth="100%"
							disableGutters={true}
							disablePaddingOn={['Top', 'Bottom']}
							backgroundColor="transparent"
							docProps={{ color: textColor }}
						/>
					)}
				</Box>
			</Grid>
			<Grid data-testid="blog_details_hero_right" item xs={12} md={6} sx={blogDetailsHeroRightStyles}>
				<Box sx={blogDetailsHeroImageWrapperStyles}>
					<BlogDetailsImageAssetContainer
						blogImage={blogImage}
						imageAsset={imageAsset}
						usePreloader={true}
						isSmallScreen={isSmallScreen}
					/>
				</Box>
				{heroContentTitle?.json && (
					<ContentTypeRichText
						content={heroContentTitle as ContentTypeRichTextContent}
						disableGutters={true}
						backgroundColor={heroBackgroundColor}
						centerAlign={false}
						disablePaddingOn={['Top', 'Bottom']}
					/>
				)}
				{hasSubContent && (
					<Box data-testid="blog_details_hero_content-list" sx={heroContentBoxStyles}>
						{heroContentCollection.items.map((item) =>
							item?.content?.json ? (
								<ContentTypeRichText
									key={item._id}
									content={item.content}
									disableGutters={true}
									backgroundColor={heroBackgroundColor}
									centerAlign={false}
									disablePaddingOn={['Top', 'Bottom']}
									docProps={{
										variant: 'bodySmallBook',
									}}
								/>
							) : null
						)}
					</Box>
				)}
				{hasButtons && (
					<Box data-testid="blog_details_hero_cta">
						{heroButtonCollection?.items?.map((button) =>
							button ? (
								<ContentfulButton
									key={button.sys.id}
									{...button}
									dataTestId="service_line_hero_cta"
									onClick={() => analyticsClick(button.analyticsContext)}
								/>
							) : null
						)}
					</Box>
				)}
			</Grid>
		</Grid>
	);
}
